export const getShadowTeamSlotKey = (lineIndex, positionIndex) =>
  `${lineIndex}:${positionIndex}`;

const SAFE_X_MIN = 18;
const SAFE_X_MAX = 82;

const SLOT_LABEL_TEMPLATES = {
  defense: {
    2: ["LCB", "RCB"],
    3: ["LCB", "CB", "RCB"],
    4: ["LB", "LCB", "RCB", "RB"],
    5: ["LWB", "LCB", "CB", "RCB", "RWB"],
  },
  defensiveMid: {
    1: ["CDM"],
    2: ["LDM", "RDM"],
    3: ["LDM", "CDM", "RDM"],
    4: ["LDM", "LCDM", "RCDM", "RDM"],
    5: ["LDM", "LCDM", "CDM", "RCDM", "RDM"],
  },
  midfield: {
    2: ["LCM", "RCM"],
    3: ["LCM", "CM", "RCM"],
    4: ["LM", "LCM", "RCM", "RM"],
    5: ["LM", "LCM", "CM", "RCM", "RM"],
  },
  attackingMid: {
    1: ["CAM"],
    2: ["LAM", "RAM"],
    3: ["LAM", "CAM", "RAM"],
    4: ["LAM", "LCAM", "RCAM", "RAM"],
    5: ["LAM", "LCAM", "CAM", "RCAM", "RAM"],
  },
  attack: {
    1: ["CF"],
    2: ["LF", "RF"],
    3: ["LW", "CF", "RW"],
    4: ["LW", "LF", "RF", "RW"],
    5: ["LW", "LF", "CF", "RF", "RW"],
  },
};

const SLOT_LABEL_PREFIXES = {
  defense: "D",
  defensiveMid: "DM",
  midfield: "M",
  attackingMid: "AM",
  attack: "A",
};

export const SHADOW_TEAM_POSITION_GROUPS = {
  GOALKEEPER: "goalkeeper",
  DEFENDER: "defender",
  MIDFIELDER: "midfielder",
  ATTACKER: "attacker",
};

export const MAX_SHADOW_TEAM_PLAYERS_PER_SLOT = 5;
export const MAX_PITCH_PLAYERS_VISIBLE_PER_SLOT = 3;
export const MAX_SHADOW_TEAM_SLOTS_PER_GROUP = 5;

const SLOT_POSITION_GROUPS = {
  gk: SHADOW_TEAM_POSITION_GROUPS.GOALKEEPER,
  defense: SHADOW_TEAM_POSITION_GROUPS.DEFENDER,
  defensiveMid: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  midfield: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  attackingMid: SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER,
  attack: SHADOW_TEAM_POSITION_GROUPS.ATTACKER,
};

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getPlayerId = (player) => toFiniteNumber(player?.id);

export const normalizeShadowTeamPositionGroup = (value) => {
  const normalizedValue = String(value ?? "").trim().toLowerCase();

  return Object.values(SHADOW_TEAM_POSITION_GROUPS).includes(normalizedValue)
    ? normalizedValue
    : null;
};

export const getShadowTeamPositionOrderStride = (positionGroup) => {
  const normalizedPositionGroup = normalizeShadowTeamPositionGroup(positionGroup);

  if (!normalizedPositionGroup) {
    return null;
  }

  return normalizedPositionGroup === SHADOW_TEAM_POSITION_GROUPS.GOALKEEPER
    ? 1
    : MAX_SHADOW_TEAM_SLOTS_PER_GROUP;
};

export const getShadowTeamPositionOrderParts = (value, positionGroup) => {
  const normalizedValue = toFiniteNumber(value);
  const normalizedStride = getShadowTeamPositionOrderStride(positionGroup);

  if (
    normalizedValue === null ||
    normalizedValue < 0 ||
    normalizedStride === null ||
    normalizedStride <= 0
  ) {
    return null;
  }

  return {
    slotOrder: normalizedValue % normalizedStride,
    slotPriority: Math.floor(normalizedValue / normalizedStride),
  };
};

export const getShadowTeamAbsolutePositionOrder = (
  slotOrder,
  slotPriority = 0,
  positionGroup
) => {
  const normalizedSlotOrder = toFiniteNumber(slotOrder);
  const normalizedSlotPriority = toFiniteNumber(slotPriority);
  const normalizedStride = getShadowTeamPositionOrderStride(positionGroup);

  if (
    normalizedSlotOrder === null ||
    normalizedSlotOrder < 0 ||
    normalizedSlotPriority === null ||
    normalizedSlotPriority < 0 ||
    normalizedStride === null ||
    normalizedStride <= 0
  ) {
    return null;
  }

  return normalizedSlotOrder + normalizedSlotPriority * normalizedStride;
};

export const normalizeFormationLines = (rawLines) => {
  if (Array.isArray(rawLines)) {
    return rawLines
      .map((line) => {
        if (typeof line === "number" || typeof line === "string") {
          return toFiniteNumber(line);
        }

        if (Array.isArray(line)) {
          return line.length;
        }

        if (line && typeof line === "object") {
          if (Array.isArray(line.positions)) {
            return line.positions.length;
          }

          return toFiniteNumber(
            line.count ?? line.players ?? line.value ?? line.length ?? line.total
          );
        }

        return null;
      })
      .filter((line) => Number.isInteger(line) && line > 0);
  }

  if (typeof rawLines === "string") {
    return rawLines
      .split(/[-/,\s]+/)
      .map((line) => toFiniteNumber(line))
      .filter((line) => Number.isInteger(line) && line > 0);
  }

  return [];
};

const getShadowTeamLineBands = (lineCount) => {
  if (lineCount <= 0) {
    return [];
  }

  if (lineCount === 1) {
    return ["attack"];
  }

  if (lineCount === 2) {
    return ["defense", "attack"];
  }

  if (lineCount === 3) {
    return ["defense", "midfield", "attack"];
  }

  if (lineCount === 4) {
    return ["defense", "defensiveMid", "attackingMid", "attack"];
  }

  if (lineCount === 5) {
    return ["defense", "defensiveMid", "midfield", "attackingMid", "attack"];
  }

  return Array.from({ length: lineCount }, (_, index) => {
    if (index === 0) {
      return "defense";
    }

    if (index === 1) {
      return "defensiveMid";
    }

    if (index === lineCount - 2) {
      return "attackingMid";
    }

    if (index === lineCount - 1) {
      return "attack";
    }

    return "midfield";
  });
};

const getFallbackSlotLabels = (band, lineSize) => {
  const prefix = SLOT_LABEL_PREFIXES[band] || "P";

  return Array.from({ length: lineSize }, (_, index) => `${prefix}${index + 1}`);
};

const getShortLabelsForBand = (band, lineSize) => {
  if (!Number.isInteger(lineSize) || lineSize <= 0) {
    return [];
  }

  return SLOT_LABEL_TEMPLATES[band]?.[lineSize] || getFallbackSlotLabels(band, lineSize);
};

const getSlotX = (lineSize, positionIndex) => {
  if (lineSize <= 1) {
    return 50;
  }

  return SAFE_X_MIN + (positionIndex * (SAFE_X_MAX - SAFE_X_MIN)) / (lineSize - 1);
};

export const buildShadowTeamSlots = (rawLines) => {
  const lines = normalizeFormationLines(rawLines);
  const lineBands = getShadowTeamLineBands(lines.length);

  const slots = [
    {
      key: getShadowTeamSlotKey(-1, 0),
      lineIndex: -1,
      positionIndex: 0,
      x: 50,
      y: 80,
      row: "gk",
      shortLabel: "GK",
      band: "gk",
      lineSize: 1,
    },
  ];

  if (!lines.length) {
    return slots;
  }

  const startY = 57;
  // Adaptive endY: fewer lines → compress so player lists don't create large empty gaps.
  // With FIELD_HEIGHT=860, ROW_H=20: 5 players list + overhead ≈ 168px → 19.5% of 860.
  // Add ~1% breathing room between list bottom and next circle → 21%.
  const MIN_LINE_SPACING_PCT = 21;
  const endY = Math.max(8, startY - (lines.length - 1) * MIN_LINE_SPACING_PCT);

  lines.forEach((lineSize, lineIndex) => {
    const band = lineBands[lineIndex] || "midfield";
    const shortLabels = getShortLabelsForBand(band, lineSize);
    const y =
      lines.length === 1
        ? (startY + endY) / 2
        : startY - (lineIndex * (startY - endY)) / (lines.length - 1);

    for (let positionIndex = 0; positionIndex < lineSize; positionIndex += 1) {
      const x = getSlotX(lineSize, positionIndex);

      slots.push({
        key: getShadowTeamSlotKey(lineIndex, positionIndex),
        lineIndex,
        positionIndex,
        x,
        y,
        row: band,
        shortLabel: shortLabels[positionIndex] || getFallbackSlotLabels(band, lineSize)[positionIndex],
        band,
        lineSize,
      });
    }
  });

  const groupCounters = {};

  return slots.map((slot) => {
    const positionGroup =
      SLOT_POSITION_GROUPS[slot.band] || SHADOW_TEAM_POSITION_GROUPS.MIDFIELDER;
    const positionOrder = groupCounters[positionGroup] ?? 0;
    groupCounters[positionGroup] = positionOrder + 1;

    return {
      ...slot,
      positionGroup,
      positionOrder,
    };
  });
};

export const mapAssignmentsBySlot = (assignments = []) => {
  return assignments.reduce((accumulator, assignment) => {
    const lineIndex = toFiniteNumber(assignment?.lineIndex);
    const positionIndex = toFiniteNumber(assignment?.positionIndex);

    if (lineIndex === null || positionIndex === null) {
      return accumulator;
    }

    const key = getShadowTeamSlotKey(lineIndex, positionIndex);
    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(assignment?.player);

    return accumulator;
  }, {});
};

export const getShadowTeamSlotForLegacyPlacement = (rawLines, lineIndex, positionIndex) => {
  const normalizedLineIndex = toFiniteNumber(lineIndex);
  const normalizedPositionIndex = toFiniteNumber(positionIndex);

  if (normalizedLineIndex === null || normalizedPositionIndex === null) {
    return null;
  }

  return (
    buildShadowTeamSlots(rawLines).find(
      (slot) =>
        slot.lineIndex === normalizedLineIndex &&
        slot.positionIndex === normalizedPositionIndex
    ) || null
  );
};

const groupShadowTeamSlotsByPosition = (slots = []) => {
  return slots.reduce((accumulator, slot) => {
    const positionGroup = normalizeShadowTeamPositionGroup(slot?.positionGroup);

    if (!positionGroup) {
      return accumulator;
    }

    if (!accumulator[positionGroup]) {
      accumulator[positionGroup] = [];
    }

    accumulator[positionGroup].push(slot);
    return accumulator;
  }, {});
};

export const resolveShadowTeamSlotForAssignment = (rawLines, assignment = {}) => {
  const slots = buildShadowTeamSlots(rawLines);
  const groupedSlots = groupShadowTeamSlotsByPosition(slots);
  const positionGroup = normalizeShadowTeamPositionGroup(assignment?.positionGroup);
  const positionOrder = toFiniteNumber(assignment?.positionOrder);

  if (positionGroup && positionOrder !== null) {
    const slotsForGroup = groupedSlots[positionGroup] || [];
    const positionOrderParts = getShadowTeamPositionOrderParts(positionOrder, positionGroup);

    if (slotsForGroup.length && positionOrderParts) {
      return (
        slotsForGroup[
          Math.min(positionOrderParts.slotOrder, slotsForGroup.length - 1)
        ] || null
      );
    }
  }

  const legacyLineIndex = toFiniteNumber(
    assignment?.legacyLineIndex ?? assignment?.lineIndex
  );
  const legacyPositionIndex = toFiniteNumber(
    assignment?.legacyPositionIndex ?? assignment?.positionIndex
  );

  if (legacyLineIndex === null || legacyPositionIndex === null) {
    return null;
  }

  return (
    slots.find(
      (slot) =>
        slot.lineIndex === legacyLineIndex &&
        slot.positionIndex === legacyPositionIndex
    ) || null
  );
};

export const resolveShadowTeamAssignmentsBySlot = (assignments = [], rawLines) => {
  const slotEntries = (Array.isArray(assignments) ? assignments : []).reduce(
    (accumulator, assignment) => {
      const player = assignment?.player;
      const playerId = getPlayerId(player);
      const slot = resolveShadowTeamSlotForAssignment(rawLines, assignment);
      const positionGroup = normalizeShadowTeamPositionGroup(assignment?.positionGroup);
      const positionOrderParts = getShadowTeamPositionOrderParts(
        assignment?.positionOrder,
        positionGroup
      );

      if (playerId === null || !slot) {
        return accumulator;
      }

      if (!accumulator[slot.key]) {
        accumulator[slot.key] = [];
      }

      accumulator[slot.key].push({
        player,
        slotPriority:
          positionOrderParts?.slotPriority ?? accumulator[slot.key].length,
        rawPositionOrder: toFiniteNumber(assignment?.positionOrder),
      });
      return accumulator;
    },
    {}
  );

  return Object.entries(slotEntries).reduce((accumulator, [slotKey, entries]) => {
    accumulator[slotKey] = entries
      .slice()
      .sort((entryA, entryB) => {
        if (entryA.slotPriority !== entryB.slotPriority) {
          return entryA.slotPriority - entryB.slotPriority;
        }

        const rawOrderA = entryA.rawPositionOrder ?? Number.MAX_SAFE_INTEGER;
        const rawOrderB = entryB.rawPositionOrder ?? Number.MAX_SAFE_INTEGER;
        return rawOrderA - rawOrderB;
      })
      .map((entry) => entry.player);

    return accumulator;
  }, {});
};

export const orderSlotPlayersByPriority = (players = [], priorityIds = []) => {
  const normalizedPlayers = Array.isArray(players) ? players.filter(Boolean) : [];
  const playersById = new Map();
  const naturalOrderIds = [];

  normalizedPlayers.forEach((player) => {
    const playerId = getPlayerId(player);

    if (playerId === null || playersById.has(playerId)) {
      return;
    }

    playersById.set(playerId, player);
    naturalOrderIds.push(playerId);
  });

  if (!naturalOrderIds.length) {
    return [];
  }

  const orderedIds = [];
  const seenIds = new Set();

  (Array.isArray(priorityIds) ? priorityIds : []).forEach((priorityId) => {
    const normalizedPriorityId = toFiniteNumber(priorityId);

    if (
      normalizedPriorityId === null ||
      !playersById.has(normalizedPriorityId) ||
      seenIds.has(normalizedPriorityId)
    ) {
      return;
    }

    seenIds.add(normalizedPriorityId);
    orderedIds.push(normalizedPriorityId);
  });

  naturalOrderIds.forEach((playerId) => {
    if (seenIds.has(playerId)) {
      return;
    }

    seenIds.add(playerId);
    orderedIds.push(playerId);
  });

  return orderedIds.map((playerId) => playersById.get(playerId));
};
